import { format, parseISO } from 'date-fns'
import type * as Api from 'src/api'

export const DEFAULT_PER_PAGE = 10
export const PER_PAGE_OPTIONS = [10, 20, 50, 100, 500, 1000].map((value) => ({ value, label: value }))

/** Router state on the link to a statement, so saving it returns to the same filtered list. */
export interface StatementLinkState {
  readonly listSearch: string
}

/** Filter values as they live in the URL and in the filter form. Empty string means "not set". */
export interface StatementsFormValues {
  readonly search: string
  readonly searchInBody: boolean
  readonly templateId: string
  readonly status: string
  readonly facId: string
  readonly degree: string
  readonly semId: string
}

const SCALAR_KEYS = ['search', 'templateId', 'status', 'facId', 'degree', 'semId'] as const

export function parseFormValues(searchParams: URLSearchParams): StatementsFormValues {
  return {
    search: searchParams.get('search') ?? '',
    searchInBody: searchParams.get('searchInBody') === '1',
    templateId: searchParams.get('templateId') ?? '',
    status: searchParams.get('status') ?? '',
    facId: searchParams.get('facId') ?? '',
    degree: searchParams.get('degree') ?? '',
    semId: searchParams.get('semId') ?? '',
  }
}

export function formValuesToSearchParams(values: StatementsFormValues, perPage: string): URLSearchParams {
  const params = new URLSearchParams()
  SCALAR_KEYS.forEach((key) => {
    const value = values[key].trim()
    if (value !== '') {
      params.set(key, value)
    }
  })
  if (values.searchInBody) {
    params.set('searchInBody', '1')
  }
  params.set('page', '1')
  params.set('perPage', perPage)

  return params
}

function toNumber(value: string | null): number | undefined {
  const parsed = parseInt(value ?? '', 10)
  return Number.isFinite(parsed) ? parsed : undefined
}

/** Empty filters are left out; without `semId` the backend uses the current semester. */
export function searchParamsToQuery(searchParams: URLSearchParams): Api.getAdministratorStatementsParams['query'] {
  const values = parseFormValues(searchParams)
  const search = values.search.trim()

  return {
    ...(search !== '' && { search }),
    ...(values.searchInBody && { searchInBody: 1 }),
    ...(toNumber(values.templateId) != null && { templateId: toNumber(values.templateId) }),
    ...(toNumber(values.status) != null && { status: toNumber(values.status) }),
    ...(toNumber(values.facId) != null && { facId: toNumber(values.facId) }),
    ...(toNumber(values.degree) != null && { degree: toNumber(values.degree) }),
    ...(toNumber(values.semId) != null && { semId: toNumber(values.semId) }),
    page: toNumber(searchParams.get('page')) ?? 1,
    perPage: toNumber(searchParams.get('perPage')) ?? DEFAULT_PER_PAGE,
  }
}

export function formatStatementDate(value: string | null | undefined): string {
  return value == null || value === '' ? '' : format(parseISO(value), 'yyyy-MM-dd HH:mm')
}
